/** The signed-in app: the rail on the left, the main panel on the backdrop, and the setup popups. */

import { useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router";

import { useAuth } from "@/auth/AuthProvider";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useMe } from "@/lib/queries";
import { useBranding } from "@/lib/useBranding";
import { HomePage } from "@/pages/Home";

import { DraftProvider } from "./draft";
import { Rail, type DialogName } from "./Rail";
import { SetupDialogs } from "./SetupDialogs";
import { TourProvider } from "./tour";

export function Shell() {
  const { state, signOut } = useAuth();
  const { brand } = useBranding();
  const [dialog, setDialog] = useState<DialogName | null>(null);
  const { pathname } = useLocation();
  const me = useMe(state.status === "signed-in");

  if (state.status === "loading") return <div className="min-h-dvh bg-black" aria-busy="true" />;
  // Signed out, `/` is the public homepage rather than a redirect to sign-in. Anywhere else still
  // needs an account, and every link the app already has keeps working for people who have one.
  if (state.status !== "signed-in") {
    return pathname === "/" ? <HomePage /> : <Navigate to="/login" replace />;
  }

  // Signed in with the identity provider, but the account itself would not open. Every other call
  // would fail the same way, so say so plainly instead of drawing a rail that does nothing.
  if (me.isError) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-4 px-4">
        <Alert variant="destructive">
          <AlertTitle>Your account could not be opened</AlertTitle>
          <AlertDescription>{me.error.message}</AlertDescription>
        </Alert>
        <div className="flex gap-2">
          <Button onClick={() => void me.refetch()}>Try again</Button>
          <Button variant="ghost" onClick={() => void signOut()}>
            Sign out
          </Button>
        </div>
      </main>
    );
  }

  return (
    <DraftProvider>
      <TourProvider>
      <div className="flex min-h-dvh flex-col bg-black md:h-dvh md:flex-row">
        <Rail onOpen={setDialog} />
        <main className="relative flex-1 overflow-y-auto">
          {/* The prototype's photograph at half opacity over black, fixed behind the content. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-50"
            style={{ backgroundImage: `url(${brand.backdrop_url})` }}
          />
          <div className="relative min-h-full">
            <Outlet />
          </div>
        </main>
        <SetupDialogs open={dialog} onClose={() => setDialog(null)} />
      </div>
      </TourProvider>
    </DraftProvider>
  );
}
